import React, { useState, useRef, useEffect, useMemo, useContext } from 'react'
import { Plus, Save, ChevronDown, ChevronUp } from 'lucide-react'
import axios from '../../../../config/axios'
import { FeedbackContext } from '../../../../contexts/FeedbackContext'
import searchIcon from '../../../../assets/router/search.svg'
import InfiniteSelect from '../../../../components/InfiniteSelect'
import crosslight from '../../../../assets/stock/FiltersHeader/cross.svg'
import trash from '../../../../assets/equipment/Tables/trash.svg'
import {
  ModalOverlay,
  ModalContainer,
  ModalHeader,
  ModalTitle,
  CloseButton,
  ModalContent,
  FormGroup,
  Label,
  Input,
  Select,
  TagsContainer,
  Tag,
  RemoveTagButton,
  GraduationsContainer,
  GraduationItem,
  GraduationHeader,
  RemoveGraduationButton,
  GradesGrid,
  GradeButton,
  AddGraduationButton,
  ModalFooter,
  CancelButton,
  SaveButton,
  SearchInputWrapper,
  SearchIcon,
  MaterialSelect,
  DropdownWrapper,
  DropdownMenu,
  DropdownItem,
  DropdownEmptyState,
} from './style'

const GATEWAY_URL = process.env.REACT_APP_GATEWAY_BASE_URL
const FASHION_API_URL = `${GATEWAY_URL}/backend-fashion-orders/fashion-product`

interface GradeType {
  id: number
  name: string
}

interface Grade {
  id: number
  name: string
  typeId: number
  type?: GradeType
  GraduationType?: GradeType
}

interface MaterialType {
  id: number
  name: string
  description?: string
}

interface Collection {
  id: number
  name: string
}

interface Group {
  id: number
  name: string
}

interface Theme {
  id: number
  name: string
  color?: string
}

interface CollectionMaterial {
  id: number
  collectionId: number
  materialId: number | null
  Material: MaterialType | null
  CollectionMaterialResources?: Array<{
    materialId?: number | null
    categoryResourceId?: number | null
    Material?: { id: number; name?: string | null } | null
    Category_Resource?: { id: number; name?: string | null } | null
  }>
}

interface FabricItem {
  id: number
  selectedCollectionMaterialId: number | null
  selectedMaterialName: string
}

interface RegisterFamilyModalProps {
  isOpen: boolean
  onClose: () => void

  onSuccess?: (savedFamily?: any) => void
  familyId?: number
  mode?: 'create' | 'edit'
}

const RegisterFamilyModal: React.FC<RegisterFamilyModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  familyId,
  mode = 'create',
}) => {
  const { setFeedback } = useContext(FeedbackContext)
  const [familyName, setFamilyName] = useState('')
  const [loading, setLoading] = useState(false)
  const [loadingData, setLoadingData] = useState(true)

  const [currentFamily, setCurrentFamily] = useState<any>(null)

  const [collectionSearch, setCollectionSearch] = useState('')
  const [selectedCollection, setSelectedCollection] = useState<Collection | null>(null)
  const [availableCollections, setAvailableCollections] = useState<Collection[]>([])
  const [showCollectionDropdown, setShowCollectionDropdown] = useState(false)
  const [loadingCollections, setLoadingCollections] = useState(false)

  const [selectedGroupIds, setSelectedGroupIds] = useState<number[]>([])
  const [filteredGroups, setFilteredGroups] = useState<Group[]>([])
  const [availableThemes, setAvailableThemes] = useState<Theme[]>([])
  const [selectedThemeIds, setSelectedThemeIds] = useState<number[]>([])
  const [showThemePicker, setShowThemePicker] = useState(false)
  const themePickerRef = useRef<HTMLDivElement | null>(null)

  const [allGrades, setAllGrades] = useState<Grade[]>([])
  const [gradesByType, setGradesByType] = useState<Record<string, Grade[]>>({})
  const [enabledGradeTypes, setEnabledGradeTypes] = useState<Record<string, boolean>>({})
  const [selectedGradeIds, setSelectedGradeIds] = useState<number[]>([])
  const [collectionGradeIds, setCollectionGradeIds] = useState<number[]>([])

  const [collectionMaterials, setCollectionMaterials] = useState<CollectionMaterial[]>([])
  const [collectionColorsAvailable, setCollectionColorsAvailable] = useState<{ id: number; name: string; hex?: string; pantoneCode?: string }[]>([])
  const [selectedCollectionColorIds, setSelectedCollectionColorIds] = useState<number[]>([])

  const [fabrics, setFabrics] = useState<FabricItem[]>([])
  const [fabricDropdowns, setFabricDropdowns] = useState<Record<number, {
    search: string
    showDropdown: boolean
    filtered: CollectionMaterial[]
  }>>({})

  const collectionRef = useRef<HTMLDivElement>(null)
  const fabricRefs = useRef<Record<number, HTMLDivElement | null>>({})

  const getAvailableMaterialsForFabric = (fabricId: number): CollectionMaterial[] => {
    const selectedIds = fabrics
      .filter(f => f.id !== fabricId && f.selectedCollectionMaterialId !== null)
      .map(f => f.selectedCollectionMaterialId)

    return collectionMaterials.filter(cm => !selectedIds.includes(cm.id))
  }

  const getFabricBaseLabel = (cm: CollectionMaterial | null | undefined): string => {
    if (!cm) return 'Material'
    return cm.Material?.name || 'Material'
  }

  const getFabricResourceTags = (cm: CollectionMaterial | null | undefined): string[] => {
    if (!cm) return []
    const links = Array.isArray(cm.CollectionMaterialResources) ? cm.CollectionMaterialResources : []
    return links
      .map((link) => {
        const categoryName = link.Category_Resource?.name || 'Categoria'
        const materialName = link.Material?.name || ''
        if (!materialName) return null
        return `${categoryName}: ${materialName}`
      })
      .filter((v): v is string => Boolean(v))
  }

  useEffect(() => {
    const fetchInitialData = async () => {
      setLoadingData(true)
      const token = localStorage.getItem('token')
      const headers = { Authorization: `Bearer ${token}` }


      try {
        let family: any = null

        if (mode === 'edit' && familyId) {

          const familyRes = await axios.get(
            `${FASHION_API_URL}/families/${familyId}`,
            { headers }
          )
          family = familyRes.data

          setCurrentFamily(family)
          setFamilyName(family.name)
          setSelectedCollection(family.Collection || family.collection)

          const groupIds = family.GroupInFamily?.map((gf: any) => gf.groupId) || []
          setSelectedGroupIds(groupIds)
        }


        const gradesRes = await axios.get(`${FASHION_API_URL}/grades`, { headers })
        const grades: Grade[] = Array.isArray(gradesRes.data) ? gradesRes.data : []

        const byType: Record<string, Grade[]> = {}
        const enabledTypes: Record<string, boolean> = {}

        grades.forEach((grade) => {
          const typeName = (grade.GraduationType || grade.type)?.name
          if (!typeName) return
          if (!byType[typeName]) {
            byType[typeName] = []
            enabledTypes[typeName] = false
          }
          byType[typeName].push(grade)
        })

        Object.keys(byType).forEach((typeName) => {
          byType[typeName].sort((a, b) => {
            const aNum = parseInt(a.name)
            const bNum = parseInt(b.name)
            if (!isNaN(aNum) && !isNaN(bNum)) {
              return aNum - bNum
            }
            return a.name.localeCompare(b.name)
          })
        })

        setAllGrades(grades)
        setGradesByType(byType)

        if (mode === 'edit' && familyId && family) {
          const materialsRes = await axios.get(
            `${FASHION_API_URL}/collections/${family.collectionId}/materials`,
            { headers }
          )
          const materials = materialsRes.data || []
          setCollectionMaterials(materials)

          const collectionGradesRes = await axios.get(
            `${FASHION_API_URL}/collections/${family.collectionId}/grades`,
            { headers }
          )
          const gradeIds = collectionGradesRes.data?.map((g: any) => g.gradeId) || []
          setCollectionGradeIds(gradeIds)

          const familyGradeIds = family.GradeInFamily?.map((fg: any) => fg.gradeId) || family.grades?.map((fg: any) => fg.gradeId) || []
          setSelectedGradeIds(familyGradeIds)

          const newEnabledTypes: Record<string, boolean> = {}
          Object.keys(byType).forEach((typeName) => {
            const hasGradesSelected = byType[typeName].some(g =>
              familyGradeIds.includes(g.id)
            )
            newEnabledTypes[typeName] = hasGradesSelected
          })
          setEnabledGradeTypes(newEnabledTypes)

          const groupsRes = await axios.get(
            `${FASHION_API_URL}/collections/${family.collectionId}/groups`,
            { headers }
          )
          const groups = Array.isArray(groupsRes.data) ? groupsRes.data.map((g: any) => g.Group || g.group) : []
          setFilteredGroups(groups.filter(Boolean))

          try {
            const themesRes = await axios.get(
              `${FASHION_API_URL}/collections/${family.collectionId}/themes`,
              { headers }
            )
            const themes: Theme[] = Array.isArray(themesRes.data) ? themesRes.data : []
            setAvailableThemes(themes)
            const familyThemeIds = (family.ThemeInFamily || []).map((t: any) => t.themeId || t.Theme?.id).filter((id: any): id is number => Boolean(id))
            setSelectedThemeIds(familyThemeIds)
          } catch (err) {
            setAvailableThemes([])
            setSelectedThemeIds([])
          }

          const fabricItems: FabricItem[] = (family.FamilyMaterial || family.materials || []).map((fm: any, index: number) => {
            const cm = fm.CollectionMaterial || fm.collectionMaterial || {}
            return {
              id: index + 1,
              selectedCollectionMaterialId: fm.collectionMaterialId,
              selectedMaterialName: getFabricBaseLabel(cm),
            }
          })

          setFabrics(fabricItems)

          const initialDropdowns: Record<number, {
            search: string
            showDropdown: boolean
            filtered: CollectionMaterial[]
          }> = {}

          fabricItems.forEach(fabric => {
            initialDropdowns[fabric.id] = {
              search: '',
              showDropdown: false,
              filtered: []
            }
          })

          setFabricDropdowns(initialDropdowns)


          try {
            const colRes = await axios.get(`${FASHION_API_URL}/collections/${family.collectionId}`, { headers })
            const colData = colRes.data
            const cols = Array.isArray(colData.CollectionColor) ? colData.CollectionColor.map((c: any) => ({ id: c.id, name: c.name, hex: c.Pantone?.hex || c.hex, pantoneCode: c.Pantone?.code })) : []
            setCollectionColorsAvailable(cols)
            const familyColorIds = (family.FamilyColor || []).map((fc: any) => fc.collectionColorId)
            setSelectedCollectionColorIds(familyColorIds)
          } catch (err) {
            setCollectionColorsAvailable([])
            setSelectedCollectionColorIds([])
          }
        } else {
          setEnabledGradeTypes(enabledTypes)
        }

      } catch (error: any) {
        console.error('❌ Erro ao carregar dados:', error)
        console.error('Status:', error.response?.status)
        console.error('Data:', error.response?.data)
        if (mode === 'edit') {
          alert('Erro ao carregar dados da família')
        }
      } finally {
        setLoadingData(false)
        console.log('✅ Carregamento concluído')
      }
    }

    if (isOpen) {
      fetchInitialData()
    }
  }, [isOpen, mode, familyId])

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (collectionRef.current && !collectionRef.current.contains(event.target as Node)) {
        setShowCollectionDropdown(false)
      }
      if (themePickerRef.current && !themePickerRef.current.contains(event.target as Node)) {
        setShowThemePicker(false)
      }

      Object.keys(fabricRefs.current).forEach(fabricId => {
        const ref = fabricRefs.current[Number(fabricId)]
        if (ref && !ref.contains(event.target as Node)) {
          setFabricDropdowns(prev => ({
            ...prev,
            [fabricId]: { ...prev[Number(fabricId)], showDropdown: false }
          }))
        }
      })
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [])

  const selectedThemes = useMemo(() => {
    return availableThemes.filter((theme) => selectedThemeIds.includes(theme.id))
  }, [availableThemes, selectedThemeIds])

  const fetchAvailableCollections = async (searchTerm: string) => {
    setLoadingCollections(true)
    try {
      const token = localStorage.getItem('token')

      const url = searchTerm.trim()
        ? `${FASHION_API_URL}/collections?search=${encodeURIComponent(searchTerm)}`
        : `${FASHION_API_URL}/collections`

      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` }
      })

      const collections = Array.isArray(response.data) ? response.data : []
      setAvailableCollections(collections)
      setShowCollectionDropdown(collections.length > 0)
    } catch (err: any) {
      console.error('❌ Erro ao buscar coleções:', err)
      setAvailableCollections([])
      setShowCollectionDropdown(false)
    } finally {
      setLoadingCollections(false)
    }
  }

  const handleCollectionSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    setCollectionSearch(value)
    fetchAvailableCollections(value)
  }

  const selectCollectionFromDropdown = async (collection: Collection) => {
    setSelectedCollection(collection)
    setCollectionSearch('')
    setAvailableCollections([])
    setShowCollectionDropdown(false)

    console.log('📦 Coleção selecionada:', collection)

    try {
      const token = localStorage.getItem('token')

      console.log('🔍 Carregando materiais da coleção...')
      const materialsRes = await axios.get(
        `${FASHION_API_URL}/collections/${collection.id}/materials`,
        { headers: { Authorization: `Bearer ${token}` } }
      )

      console.log('✅ Materiais recebidos:', materialsRes.data)
      setCollectionMaterials(materialsRes.data || [])

      console.log('🔍 Carregando grades da coleção...')
      const gradesRes = await axios.get(
        `${FASHION_API_URL}/collections/${collection.id}/grades`,
        { headers: { Authorization: `Bearer ${token}` } }
      )

      console.log('✅ Grades recebidas:', gradesRes.data)
      const gradeIds = gradesRes.data?.map((g: any) => g.gradeId) || []
      setCollectionGradeIds(gradeIds)
      setSelectedGradeIds([])
      setSelectedGroupIds([])

      const newEnabledTypes: Record<string, boolean> = {}
      Object.keys(gradesByType).forEach((typeName) => {
        newEnabledTypes[typeName] = false
      })
      setEnabledGradeTypes(newEnabledTypes)
      console.log('✅ Tipos habilitados:', newEnabledTypes)

      console.log('🔍 Carregando grupos da coleção...')
      const groupsRes = await axios.get(
        `${FASHION_API_URL}/collections/${collection.id}/groups`,
        { headers: { Authorization: `Bearer ${token}` } }
      )

      const groups = Array.isArray(groupsRes.data) ? groupsRes.data.map((g: any) => g.Group || g.group).filter(Boolean) : []
      setFilteredGroups(groups)

      try {
        const themesRes = await axios.get(
          `${FASHION_API_URL}/collections/${collection.id}/themes`,
          { headers: { Authorization: `Bearer ${token}` } }
        )
        const themes: Theme[] = Array.isArray(themesRes.data) ? themesRes.data : []
        setAvailableThemes(themes)
        setSelectedThemeIds([])
      } catch (err) {
        setAvailableThemes([])
        setSelectedThemeIds([])
      }

      try {
        const colRes = await axios.get(`${FASHION_API_URL}/collections/${collection.id}`, { headers: { Authorization: `Bearer ${token}` } })
        const colData = colRes.data
        const cols = Array.isArray(colData.CollectionColor) ? colData.CollectionColor.map((c: any) => ({ id: c.id, name: c.name, hex: c.Pantone?.hex || c.hex, pantoneCode: c.Pantone?.code })) : []
        setCollectionColorsAvailable(cols)
        setSelectedCollectionColorIds([])
      } catch (err) {
        setCollectionColorsAvailable([])
        setSelectedCollectionColorIds([])
      }

    } catch (error: any) {
      console.error('Erro ao carregar dados da coleção:', error)
    }
  }

  const handleCollectionKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      if (availableCollections.length > 0) {
        selectCollectionFromDropdown(availableCollections[0])
      }
    }
  }

  const handleRemoveCollection = () => {
    if (mode === 'edit') {
      return
    }

    setSelectedCollection(null)
    setCollectionMaterials([])
    setFabrics([])
    setSelectedGradeIds([])
    setEnabledGradeTypes({})
    setCollectionGradeIds([])
    setFilteredGroups([])
    setSelectedGroupIds([])
    setAvailableThemes([])
    setSelectedThemeIds([])
  }

  const toggleGradeType = (typeName: string, enabled: boolean) => {
    setEnabledGradeTypes((prev) => ({ ...prev, [typeName]: enabled }))
    if (!enabled) {
      const gradeIdsOfType = gradesByType[typeName]?.map((g) => g.id) || []
      setSelectedGradeIds((prev) => prev.filter((id) => !gradeIdsOfType.includes(id)))
    }
  }

  const toggleGrade = (gradeId: number, typeName: string) => {
    if (!enabledGradeTypes[typeName]) return
    setSelectedGradeIds((prev) =>
      prev.includes(gradeId) ? prev.filter((id) => id !== gradeId) : [...prev, gradeId]
    )
  }

  const toggleGroup = (groupId: number) => {
    setSelectedGroupIds((prev) =>
      prev.includes(groupId) ? prev.filter((id) => id !== groupId) : [...prev, groupId]
    )
  }

  const removeGroup = (groupId: number) => {
    setSelectedGroupIds((prev) => prev.filter((id) => id !== groupId))
  }

  const addFabric = () => {
    const newId = fabrics.length > 0 ? Math.max(...fabrics.map((f) => f.id)) + 1 : 1
    console.log('➕ Adicionando novo fabric com ID:', newId)

    const available = getAvailableMaterialsForFabric(newId)

    setFabrics([
      ...fabrics,
      {
        id: newId,
        selectedCollectionMaterialId: null,
        selectedMaterialName: '',
      },
    ])

    setFabricDropdowns(prev => ({
      ...prev,
      [newId]: {
        search: '',
        showDropdown: false,
        filtered: available
      }
    }))
  }

  const removeFabric = (id: number) => {
    console.log('🗑️ Removendo fabric ID:', id)
    setFabrics((prevFabrics) => prevFabrics.filter((f) => f.id !== id))
    setFabricDropdowns(prev => {
      const newState = { ...prev }
      delete newState[id]
      return newState
    })
    delete fabricRefs.current[id]
  }

  const handleFabricSearchChange = (fabricId: number, value: string) => {
    const available = getAvailableMaterialsForFabric(fabricId)
    const filtered = available.filter(cm => {
      const materialName = cm.Material?.name || ''
      return materialName.toLowerCase().includes(value.toLowerCase())
    })

    setFabricDropdowns(prev => ({
      ...prev,
      [fabricId]: {
        search: value,
        showDropdown: true,
        filtered
      }
    }))
  }

  const selectFabricMaterial = async (fabricId: number, collectionMaterial: CollectionMaterial) => {
    setFabrics((prevFabrics) =>
      prevFabrics.map((f) =>
        f.id === fabricId
          ? {
            ...f,
            selectedCollectionMaterialId: collectionMaterial.id,
            selectedMaterialName: getFabricBaseLabel(collectionMaterial),
          }
          : f
      )
    )

    setFabricDropdowns(prev => ({
      ...prev,
      [fabricId]: {
        ...prev[fabricId],
        search: '',
        showDropdown: false
      }
    }))
  }

  const handleRemoveFabricMaterial = (fabricId: number) => {
    setFabrics((prevFabrics) =>
      prevFabrics.map((f) =>
        f.id === fabricId
          ? {
            ...f,
            selectedCollectionMaterialId: null,
            selectedMaterialName: '',
          }
          : f
      )
    )
  }

  const handleSave = async () => {
    if (!familyName.trim()) {
      setFeedback({
        isOpen: true,
        statusOk: false,
        message: 'Por favor, preencha o nome da família.',
      })
      return
    }

    if (!selectedCollection) {
      setFeedback({
        isOpen: true,
        statusOk: false,
        message: 'Por favor, selecione uma coleção.',
      })
      return
    }

    if (selectedGradeIds.length === 0) {
      setFeedback({
        isOpen: true,
        statusOk: false,
        message: 'É obrigatório selecionar pelo menos uma grade na família.',
      })
      return
    }

    if (fabrics.length === 0 || !fabrics.some(f => f.selectedCollectionMaterialId)) {
      setFeedback({
        isOpen: true,
        statusOk: false,
        message: 'Por favor, adicione pelo menos um material.',
      })
      return
    }

    setLoading(true)

    try {
      const materials = fabrics
        .filter((f) => f.selectedCollectionMaterialId)
        .map((f) => ({
          collectionMaterialId: f.selectedCollectionMaterialId!,
        }))

      const payload = {
        name: familyName,
        collectionId: selectedCollection.id,
        groupIds: selectedGroupIds,
        themeIds: selectedThemeIds,
        gradeIds: selectedGradeIds,
        materials,
        collectionColorIds: selectedCollectionColorIds,
      }


      const token = localStorage.getItem('token')

      let response
      console.debug('saving family payload', payload)
      if (mode === 'edit' && familyId) {
        response = await axios.put(
          `${FASHION_API_URL}/families/${familyId}`,
          payload,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
          }
        )
        console.debug('update response', response.data)
        setFeedback({ isOpen: true, statusOk: true, message: 'Família atualizada com sucesso!' })
      } else {
        response = await axios.post(`${FASHION_API_URL}/families`, payload, {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        })
        console.debug('create response', response.data)
        setFeedback({ isOpen: true, statusOk: true, message: 'Família criada com sucesso!' })
      }


      const saved = response?.data?.data || response?.data
      console.debug('saved family object', saved)
      onSuccess?.(saved)
      onClose()
    } catch (err: any) {
      console.error('Erro ao salvar família:', err)
      const action = mode === 'edit' ? 'atualizar' : 'criar'
      setFeedback({
        isOpen: true,
        statusOk: false,
        message: err.response?.data?.message || `Erro ao ${action} família. Tente novamente.`,
      })
    } finally {
      setLoading(false)
    }
  }

  if (!isOpen) return null

  if (loadingData) {
    return (
      <ModalOverlay onClick={onClose}>
        <ModalContainer onClick={(e) => e.stopPropagation()}>
          <p style={{ padding: '20px', textAlign: 'center' }}>Carregando dados...</p>
        </ModalContainer>
      </ModalOverlay>
    )
  }

  return (
    <ModalOverlay onClick={onClose}>
      <ModalContainer onClick={(e) => e.stopPropagation()}>
        <ModalHeader>
          <ModalTitle>{mode === 'edit' ? 'Editar família' : 'Nova família'}</ModalTitle>
          <CloseButton onClick={onClose}>
            <img src={crosslight} alt='Close' />
          </CloseButton>
        </ModalHeader>

        <ModalContent>

          <FormGroup>
            <Label>Coleção</Label>
            <div style={{ position: 'relative' }} ref={collectionRef}>
              <SearchInputWrapper>
                <Input
                  type='text'
                  placeholder='Procure aqui'
                  value={selectedCollection ? selectedCollection.name : collectionSearch}
                  onChange={handleCollectionSearchChange}
                  onKeyPress={handleCollectionKeyPress}
                  onFocus={() => {
                    if (!selectedCollection && mode !== 'edit') {
                      fetchAvailableCollections(collectionSearch)
                    }
                  }}
                  readOnly={!!selectedCollection || mode === 'edit'}
                  style={{
                    cursor: (selectedCollection || mode === 'edit') ? 'default' : 'text'
                  }}
                />
                {selectedCollection && mode !== 'edit' ? (
                  <SearchIcon
                    style={{ pointerEvents: 'all', cursor: 'pointer' }}
                    onClick={handleRemoveCollection}
                  >
                    <img src={crosslight} alt='Limpar' style={{ width: '14px', height: '14px' }} />
                  </SearchIcon>
                ) : mode === 'edit' ? (
                  <SearchIcon style={{ pointerEvents: 'none' }}>
                    <img src={searchIcon} alt='Buscar' />
                  </SearchIcon>
                ) : (
                  <SearchIcon style={{ pointerEvents: 'none' }}>
                    <img src={searchIcon} alt='Buscar' />
                  </SearchIcon>
                )}
              </SearchInputWrapper>

              {showCollectionDropdown && !selectedCollection && mode !== 'edit' && (
                <div
                  style={{
                    position: 'absolute',
                    top: '100%',
                    left: 0,
                    right: 0,
                    background: 'white',
                    border: '1px solid #e5e7eb',
                    borderRadius: '8px',
                    marginTop: '4px',
                    maxHeight: '200px',
                    overflowY: 'auto',
                    zIndex: 1000,
                    boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
                  }}
                >
                  {loadingCollections ? (
                    <div style={{ padding: '10px 16px', color: '#6b7280' }}>Buscando...</div>
                  ) : availableCollections.length > 0 ? (
                    availableCollections.map((collection) => (
                      <div
                        key={collection.id}
                        onClick={() => selectCollectionFromDropdown(collection)}
                        style={{
                          padding: '10px 16px',
                          cursor: 'pointer',
                          borderBottom: '1px solid #f3f4f6',
                        }}
                        onMouseEnter={(e) => (e.currentTarget.style.background = '#f9fafb')}
                        onMouseLeave={(e) => (e.currentTarget.style.background = 'white')}
                      >
                        {collection.name}
                      </div>
                    ))
                  ) : (
                    <div style={{ padding: '10px 16px', color: '#6b7280' }}>
                      Nenhuma coleção encontrada
                    </div>
                  )}
                </div>
              )}
            </div>
          </FormGroup>




          <FormGroup>
            <Label>Nome da família</Label>
            <Input
              type='text'
              placeholder='Digite aqui'
              value={familyName}
              onChange={(e) => setFamilyName(e.target.value)}
            />
          </FormGroup>


          {selectedCollection && filteredGroups.length > 0 && (
            <>
              <FormGroup>
                <Label>Selecionar grupos</Label>
                <Select
                  value=''
                  onChange={(e) => {
                    const selectedId = Number(e.target.value)
                    if (selectedId && !selectedGroupIds.includes(selectedId)) {
                      setSelectedGroupIds([...selectedGroupIds, selectedId])
                    }
                  }}
                >
                  <option value=''>Selecione um grupo</option>
                  {filteredGroups
                    .filter(g => !selectedGroupIds.includes(g.id))
                    .map((g) => (
                      <option key={g.id} value={g.id}>
                        {g.name}
                      </option>
                    ))}
                </Select>

                {selectedGroupIds.length > 0 && (
                  <TagsContainer>
                    {selectedGroupIds.map((groupId) => {
                      const group = filteredGroups.find(g => g.id === groupId)
                      if (!group) return null
                      return (
                        <Tag key={groupId}>
                          {group.name}
                          <RemoveTagButton onClick={() => removeGroup(groupId)}>
                            <img src={crosslight} alt='Remover' style={{ width: '14px', height: '14px' }} />
                          </RemoveTagButton>
                        </Tag>
                      )
                    })}
                  </TagsContainer>
                )}
              </FormGroup>
            </>
          )}

          {selectedCollection && availableThemes.length > 0 && (
            <>
              <FormGroup>
                <Label>Temas</Label>
                <div style={{ position: 'relative', width: '100%' }} ref={themePickerRef}>
                  <div
                    onClick={() => setShowThemePicker((prev) => !prev)}
                    style={{
                      width: '100%',
                      borderRadius: '100px',
                      border: '1px solid #ebebeb',
                      backgroundColor: '#ebebeb',
                      color: '#333',
                      padding: '8px 12px',
                      fontSize: '14px',
                      minHeight: '40px',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      boxSizing: 'border-box',
                    }}
                  >
                    <span style={{ color: '#9ca3af' }}>
                      {selectedThemeIds.length > 0 ? `${selectedThemeIds.length} tema(s) selecionado(s)` : 'Selecione temas'}
                    </span>
                    {showThemePicker ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                  </div>

                  {showThemePicker && (
                    <div style={{ position: 'absolute', top: 'calc(100% + 4px)', left: 0, right: 0, zIndex: 999, background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8, boxShadow: '0 4px 10px rgba(0,0,0,0.15)', maxHeight: '250px', overflowY: 'auto' }}>
                      {(() => {
                        const available = availableThemes
                          .filter((theme) => !selectedThemeIds.includes(theme.id))

                        if (available.length === 0) {
                          return <div style={{ padding: '8px', color: '#666' }}>Nenhum tema disponível</div>
                        }

                        return available.map((theme) => (
                          <div
                            key={theme.id}
                            onClick={() => {
                              setSelectedThemeIds((prev) => [...prev, theme.id])
                              setShowThemePicker(false)
                            }}
                            style={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                              padding: '8px 12px',
                              cursor: 'pointer',
                              background: '#fff',
                            }}
                          >
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                              <span
                                style={{
                                  width: 10,
                                  height: 10,
                                  borderRadius: '50%',
                                  backgroundColor: theme.color || '#999',
                                  display: 'inline-block',
                                }}
                              />
                              <span>{theme.name}</span>
                            </div>
                            <span style={{ color: '#d1d5db' }}>
                              {''}
                            </span>
                          </div>
                        ))
                      })()}
                    </div>
                  )}
                </div>

                {selectedThemeIds.length > 0 && (
                  <TagsContainer style={{ marginTop: '8px' }}>
                    {selectedThemeIds.map((themeId) => {
                      const theme = availableThemes.find((t) => t.id === themeId)
                      if (!theme) return null
                      return (
                        <Tag key={themeId} style={{ backgroundColor: theme.color ? `${theme.color}22` : '#ffffff', borderColor: theme.color ? theme.color : '#e0e0e0', color: '#222' }}>
                          <span
                            style={{
                              width: 10,
                              height: 10,
                              borderRadius: '50%',
                              backgroundColor: theme.color || '#999',
                              display: 'inline-block',
                              marginRight: 8,
                              border: '1px solid #fff',
                            }}
                          />
                          <span>{theme.name}</span>
                          <RemoveTagButton onClick={() => setSelectedThemeIds((prev) => prev.filter((id) => id !== themeId))}>
                            <img src={crosslight} alt='Remover' style={{ width: '14px', height: '14px' }} />
                          </RemoveTagButton>
                        </Tag>
                      )
                    })}
                  </TagsContainer>
                )}
              </FormGroup>
            </>
          )}

          {selectedCollection && (
            <>
              <FormGroup>
                <Label>Cores da coleção</Label>
                <Select
                  value=''
                  onChange={(e) => {
                    const selectedId = Number(e.target.value)
                    if (selectedId && !selectedCollectionColorIds.includes(selectedId)) {
                      setSelectedCollectionColorIds([...selectedCollectionColorIds, selectedId])
                    }
                  }}
                >
                  <option value=''>Selecione uma cor</option>
                  {collectionColorsAvailable
                    .filter(c => !selectedCollectionColorIds.includes(c.id))
                    .map(c => (
                      <option key={c.id} value={c.id}>{c.name}</option>
                    ))}
                </Select>

                {selectedCollectionColorIds.length > 0 && (
                  <TagsContainer>
                    {selectedCollectionColorIds.map((colId) => {
                      const col = collectionColorsAvailable.find(c => Number(c.id) === Number(colId))
                      if (!col) return null
                      return (
                        <Tag key={colId} style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                          {col.hex && (
                            <span
                              style={{
                                display: 'inline-block',
                                width: 14,
                                height: 14,
                                borderRadius: 3,
                                backgroundColor: col.hex.startsWith('#') ? col.hex : `#${col.hex}`,
                                border: '1px solid rgba(0,0,0,0.15)',
                              }}
                              title={col.hex}
                            />
                          )}
                          <span>{col.name}</span>
                          {col.pantoneCode && (
                            <span style={{ fontSize: 10, color: '#888' }}>({col.pantoneCode})</span>
                          )}
                          <RemoveTagButton onClick={() => setSelectedCollectionColorIds(prev => prev.filter(id => Number(id) !== Number(colId)))}>
                            <img src={crosslight} alt='Remover' style={{ width: '14px', height: '14px' }} />
                          </RemoveTagButton>
                        </Tag>
                      )
                    })}
                  </TagsContainer>
                )}
              </FormGroup>
            </>
          )}


          {selectedCollection && (
            <>
              <FormGroup>
                <Label>Tipo de graduação</Label>
                <Select
                  value={''}
                  onChange={(e) => {
                    const typeName = e.target.value
                    if (typeName && !enabledGradeTypes[typeName]) {
                      toggleGradeType(typeName, true)
                    }
                  }}
                >
                  <option value=''>Selecione</option>
                  {Object.entries(gradesByType).map(([typeName, grades]) => {
                    const gradesInCollection = grades.filter(g => collectionGradeIds.includes(g.id))
                    if (gradesInCollection.length === 0 || enabledGradeTypes[typeName]) return null
                    return (
                      <option key={typeName} value={typeName}>
                        {typeName}
                      </option>
                    )
                  })}
                </Select>

                {Object.keys(enabledGradeTypes).some(t => enabledGradeTypes[t]) && (
                  <TagsContainer>
                    {Object.entries(enabledGradeTypes).map(([typeName, enabled]) => {
                      if (!enabled) return null
                      return (
                        <Tag key={typeName}>
                          {typeName}
                          <RemoveTagButton onClick={() => toggleGradeType(typeName, false)}>
                            <img src={crosslight} alt='Remover' style={{ width: '14px', height: '14px' }} />
                          </RemoveTagButton>
                        </Tag>
                      )
                    })}
                  </TagsContainer>
                )}
              </FormGroup>


              {Object.entries(gradesByType).map(([typeName, grades]) => {
                if (!enabledGradeTypes[typeName]) return null

                const gradesInCollection = grades.filter(g => collectionGradeIds.includes(g.id))
                if (gradesInCollection.length === 0) return null

                return (
                  <FormGroup key={typeName}>
                    <Label>Graduação - {typeName}</Label>
                    <GradesGrid>
                      {gradesInCollection.map((grade) => (
                        <GradeButton
                          key={grade.id}
                          type='button'
                          $isSelected={selectedGradeIds.includes(grade.id)}
                          onClick={() => toggleGrade(grade.id, typeName)}
                        >
                          {grade.name}
                        </GradeButton>
                      ))}
                    </GradesGrid>
                  </FormGroup>
                )
              })}
            </>
          )}

          {selectedCollection && collectionMaterials.length > 0 && (
            <FormGroup>
              <Label>Tecidos e Aviamentos</Label>

              <GraduationsContainer>
                {fabrics.map((fabric) => {
                  const currentMaterial = fabric.selectedCollectionMaterialId
                    ? collectionMaterials.find(cm => cm.id === fabric.selectedCollectionMaterialId)
                    : null

                  const dropdownState = fabricDropdowns[fabric.id] || {
                    search: '',
                    showDropdown: false,
                    filtered: getAvailableMaterialsForFabric(fabric.id)
                  }

                  return (
                    <GraduationItem key={fabric.id}>
                      <GraduationHeader>
                        <div style={{ flex: 1 }}>
                          <DropdownWrapper ref={el => fabricRefs.current[fabric.id] = el}>
                            <SearchInputWrapper>
                              <Input
                                type='text'
                                placeholder='Procure aqui'
                                value={
                                  currentMaterial
                                    ? getFabricBaseLabel(currentMaterial)
                                    : dropdownState.search
                                }
                                onChange={(e) => handleFabricSearchChange(fabric.id, e.target.value)}
                                onFocus={() => {
                                  if (!currentMaterial) {
                                    const available = getAvailableMaterialsForFabric(fabric.id)
                                    setFabricDropdowns(prev => ({
                                      ...prev,
                                      [fabric.id]: {
                                        ...prev[fabric.id],
                                        showDropdown: true,
                                        filtered: available
                                      }
                                    }))
                                  }
                                }}
                                readOnly={!!currentMaterial}
                                style={{
                                  cursor: currentMaterial ? 'default' : 'text'
                                }}
                              />
                              {currentMaterial ? (
                                <SearchIcon
                                  style={{ pointerEvents: 'all', cursor: 'pointer' }}
                                  onClick={() => handleRemoveFabricMaterial(fabric.id)}
                                >
                                  <img src={crosslight} alt='Limpar' style={{ width: '14px', height: '14px' }} />
                                </SearchIcon>
                              ) : (
                                <SearchIcon style={{ pointerEvents: 'none' }}>
                                  <img src={searchIcon} alt='Buscar' />
                                </SearchIcon>
                              )}
                            </SearchInputWrapper>

                            {dropdownState.showDropdown && !currentMaterial && (
                              <DropdownMenu>
                                {dropdownState.filtered.length > 0 ? (
                                  dropdownState.filtered.map((cm) => (
                                    <DropdownItem
                                      key={cm.id}
                                      onClick={() => selectFabricMaterial(fabric.id, cm)}
                                    >
                                      {getFabricBaseLabel(cm) || `Material #${cm.id}`}
                                    </DropdownItem>
                                  ))
                                ) : (
                                  <DropdownEmptyState>
                                    {getAvailableMaterialsForFabric(fabric.id).length === 0
                                      ? 'Todos os materiais já foram selecionados'
                                      : 'Nenhum material encontrado'}
                                  </DropdownEmptyState>
                                )}
                              </DropdownMenu>
                            )}
                          </DropdownWrapper>
                        </div>

                        <RemoveGraduationButton onClick={() => removeFabric(fabric.id)}>
                          <img src={trash} alt='Excluir' />
                        </RemoveGraduationButton>
                      </GraduationHeader>

                      {currentMaterial && getFabricResourceTags(currentMaterial).length > 0 && (
                        <TagsContainer>
                          {getFabricResourceTags(currentMaterial).map((tag, index) => (
                            <Tag key={`${fabric.id}-resource-tag-${index}`}>{tag}</Tag>
                          ))}
                        </TagsContainer>
                      )}

                    </GraduationItem>
                  )
                })}

                <AddGraduationButton onClick={addFabric}>
                  <Plus />
                  Adicionar material
                </AddGraduationButton>
              </GraduationsContainer>
            </FormGroup>
          )}
        </ModalContent>

        <ModalFooter>
          <CancelButton onClick={onClose} disabled={loading}>Cancelar</CancelButton>
          <SaveButton onClick={handleSave} disabled={loading}>
            <Save />
            {loading
              ? 'Salvando...'
              : mode === 'edit'
                ? 'Salvar alterações'
                : 'Salvar família'}
          </SaveButton>
        </ModalFooter>
      </ModalContainer>
    </ModalOverlay>
  )
}

export default RegisterFamilyModal